import { useState, type ReactNode } from 'react';

import { Chevron } from 'components/Chevron/Chevron';

export interface DetailsSectionProps {
  readonly title: string;
  /** One sentence standing in for the content while the section is collapsed. */
  readonly summary?: ReactNode;
  readonly defaultCollapsed?: boolean;
  readonly children: ReactNode;
}

/**
 * A titled group of a details pane. Collapsed, it shows its summary sentence;
 * expanded, its content (typically a DetailsGrid). The summary and the content must
 * be derived from the same data, or the two can disagree.
 */
export const DetailsSection = ({ title, summary, defaultCollapsed = false, children }: DetailsSectionProps) => {
  const [expanded, setExpanded] = useState(!defaultCollapsed);
  return (
    <div className="details-section">
      <button type="button" className="details-section__toggle" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}>
        <Chevron expanded={expanded} className="details-section__chevron" />
        <span className="details-section__title">{title}</span>
        {!expanded && summary && <span className="details-section__summary">{summary}</span>}
      </button>
      {expanded && <div className="details-section__content">{children}</div>}
    </div>
  );
};

/** An untitled, always-open group: for what describes the object as a whole. */
export const DetailsBlock = ({ children }: { readonly children: ReactNode }) => <div className="details-section">{children}</div>;
